"use client";

import { useState } from "react";
import { Info } from "lucide-react";
import { displayCurrency } from "@/lib/utils";
import type { OrderTotals } from "@/lib/cart/types";
import { useFees } from "@/context/fees-context";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function OrderSummaryLines({ totals }: { totals: OrderTotals }) {
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [serviceFeeDialogOpen, setServiceFeeDialogOpen] = useState(false);
  const rawFees = useFees();
  const freeServiceThreshold: number | null = rawFees?.MIN_ORDER_AMOUNT ?? null;

  return (
    <div className="space-y-3 text-sm">
      <div className="flex justify-between">
        <span className="text-muted-foreground">Subtotal</span>
        <span>{displayCurrency(totals.subtotal)}</span>
      </div>
      {totals.couponDiscount > 0 && (
        <div className="flex justify-between text-teal-600">
          <span>Descuento</span>
          <span>-{displayCurrency(totals.couponDiscount)}</span>
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-muted-foreground">Envío</span>
        <span>{displayCurrency(totals.deliveryFee)}</span>
      </div>
      {totals.expressDeliveryFee > 0 && (
        <div className="flex justify-between">
          <span className="text-muted-foreground">Envío Express</span>
          <span>{displayCurrency(totals.expressDeliveryFee)}</span>
        </div>
      )}
      {totals.serviceFee > 0 && (
        <>
          <div className="flex justify-between">
            <button
              type="button"
              className="flex items-center gap-1 text-muted-foreground"
              onClick={() => setServiceFeeDialogOpen(true)}
            >
              Servicio
              <Info className="size-3.5 cursor-pointer" />
            </button>
            <span>{displayCurrency(totals.serviceFee)}</span>
          </div>
          <Dialog open={serviceFeeDialogOpen} onOpenChange={setServiceFeeDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Servicio pedido pequeño</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Se agrega una cuota de servicio de {displayCurrency(totals.serviceFee)} en pedidos
                con subtotal menor a {freeServiceThreshold !== null ? displayCurrency(freeServiceThreshold) : "—"}.
                Pide {freeServiceThreshold !== null ? displayCurrency(freeServiceThreshold) : "—"} o más para eliminar esta cuota.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setServiceFeeDialogOpen(false)}>Cerrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
      {totals.balanceApplied > 0 && (
        <>
          <div className="flex justify-between text-teal-600">
            <button
              type="button"
              className="flex items-center gap-1"
              onClick={() => setBalanceDialogOpen(true)}
            >
              Saldo a favor
              <Info className="size-3.5 cursor-pointer" />
            </button>
            <span>-{displayCurrency(totals.balanceApplied)}</span>
          </div>
          <Dialog open={balanceDialogOpen} onOpenChange={setBalanceDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Balance</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Tu balance incluye reembolsos y créditos
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBalanceDialogOpen(false)}>Cerrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
      <div className="border-t pt-3 flex justify-between font-semibold text-base">
        <span>Total</span>
        <span>{displayCurrency(totals.total)}</span>
      </div>
    </div>
  );
}
